import {
  Box,
  Typography,
  TextField,
  Divider,
  MenuItem,
  Select,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';

import {
  DataGrid
} from '@mui/x-data-grid';

import { useState, useEffect } from 'react';

const chequeTypes = ['BT', 'PURCHASE'];

const documentTypes = [
  'Original',
  'Xerox',
  'True Copy',
  'Online Certified Copy',
  'Manual Certified Copy',
  'E-copy',
  'SRO E-copy'
];

const documentStatuses = [
  'Received',
  'Verified',
  'Pending',
  'Handover',
  'In Yaazh Office'
];

const expenseRows = [
  'PETROL',
  'FOOD',
  'STATIONARY'
];

const ChequeHandoverForm = ({
  onChange,
  user,
  initialData
}) => {

  const [dialogOpen, setDialogOpen] = useState(false);

  const emptyDoc = {
    id: Date.now(),
    doc_date_number: '',
    description: '',
    document_type: 'Original',
    handover_to: '',
    handover_to_date: '',
    handover_by: '',
    handover_by_date: '',
    document_status: 'Received'
  };

  const [editingDoc, setEditingDoc] = useState(emptyDoc);

  const [form, setForm] = useState({
    meta: {
      employee_name: user?.name || '',
      emp_id: user?.id || '',
      date: new Date().toLocaleDateString()
    },

    category: 'CHEQUE_HANDOVER',

    cheque_handover_status: 'PENDING',

    customer: {
      name: '',
      mobile: '',
      reference_staff: '',
      cheque_type: 'BT',
      from_bank: '',
      to_bank_sro: ''
    },

    acknowledgement: {
      handover_to: '',
      handover_by: ''
    },

    document_details: [],

    expenses: expenseRows.map(r => ({
      label: r,
      amount: ''
    }))
  });

  // 🔥 EDIT MODE
  useEffect(() => {

    if (initialData) {

      setForm(prev => ({
        ...prev,
        ...initialData,
        meta: {
          ...prev.meta,
          ...(initialData?.meta || {})
        }
      }));
    }

  }, [initialData]);

  // 🔥 SEND TO PARENT
  useEffect(() => {

    const total =
      form.expenses.reduce(
        (s, r) => s + Number(r.amount || 0),
        0
      );

    onChange({
      ...form,
      total
    });

  }, [form]);

  // 🔥 UPDATE
  const updateCustomer = (key, value) => {
    setForm(prev => ({
      ...prev,
      customer: {
        ...prev.customer,
        [key]: value
      }
    }));
  };

  const updateAcknowledgement = (key, value) => {
    setForm(prev => ({
      ...prev,
      acknowledgement: {
        ...prev.acknowledgement,
        [key]: value
      }
    }));
  };

  const updateExpense = (i, value) => {

    const copy = [...form.expenses];
    copy[i].amount = value;

    setForm(prev => ({
      ...prev,
      expenses: copy
    }));
  };

  // 🔥 GRID SAVE
  const saveDocument = () => {

    let docs = [...form.document_details];

    const index =
      docs.findIndex(d => d.id === editingDoc.id);

    if (index >= 0) {
      docs[index] = editingDoc;
    } else {
      docs.push(editingDoc);
    }

    setForm(prev => ({
      ...prev,
      document_details: docs
    }));

    setDialogOpen(false);

    setEditingDoc({
      ...emptyDoc,
      id: Date.now()
    });
  };

  // 🔥 GRID EDIT
  const handleEditDoc = (row) => {
    setEditingDoc(row);
    setDialogOpen(true);
  };

  const total =
    form.expenses.reduce(
      (s, r) => s + Number(r.amount || 0),
      0
    );

    // 🔥 GRID COLUMNS
    const columns = [
        {
            field: 'sl_no',
            headerName: 'SL.NO',
            width: 90,
            sortable: false,
            filterable: false,
            renderCell: (params) =>
                params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
        },
        {
            field: 'doc_date_number',
            headerName: 'DOC DATE & NUMBER',
            width: 220
        },
        {
            field: 'description',
            headerName: 'DESCRIPTION OF DOCUMENT',
            width: 240
        },
        {
            field: 'document_type',
            headerName: 'TYPE OF DOCUMENT',
            width: 220
        },
        {
            field: 'handover_to',
            headerName: 'HANDOVERED TO',
            width: 180
        },
        {
            field: 'handover_to_date',
            headerName: 'HANDOVER TO DATE',
            width: 170,
            renderCell: (params) =>
                params.row?.handover_to_date
                    ? new Date(params.row.handover_to_date)
                        .toLocaleDateString('en-GB')
                    : ''
        },
        {
            field: 'handover_by',
            headerName: 'HANDOVERED BY',
            width: 180
        },

        {
            field: 'handover_by_date',
            headerName: 'HANDOVER BY DATE',
            width: 170,
            renderCell: (params) =>
                params.row?.handover_by_date
                    ? new Date(params.row.handover_by_date)
                        .toLocaleDateString('en-GB')
                    : ''
        },
        {
            field: 'document_status',
            headerName: 'DOCUMENT STATUS',
            width: 170
        }
    ];

  return (
    <Box>

      {/* 🔥 TITLE */}
      <Typography
        align="center"
        fontWeight={700}
        mb={3}
      >
        Cheque Handover Expenses
      </Typography>

      {/* 🔥 HEADER */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr 1fr',
          gap: 2,
          mb: 3
        }}
      >
        <TextField
          label="Employee Name"
          value={form.meta.employee_name}
          disabled
        />

        <TextField
          label="Employee ID"
          value={form.meta.emp_id}
          disabled
        />

        <TextField
          label="Date"
          value={form.meta.date}
          disabled
        />
      </Box>

      {/* 🔥 CATEGORY */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: 2,
          mb: 3
        }}
      >
        <TextField
          label="Category"
          value="Cheque Handover"
          disabled
        />

        <Select
          value={form.cheque_handover_status}
          onChange={(e) =>
            setForm({
              ...form,
              cheque_handover_status: e.target.value
            })
          }
        >
          <MenuItem value="PENDING">
            Pending
          </MenuItem>
          <MenuItem value="COMPLETED">
            Completed
          </MenuItem>

        </Select>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* 🔥 CUSTOMER DETAILS */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 2,
          mb: 3
        }}
      >

        {/* LEFT */}
        <Box
          sx={{
            display: 'grid',
            gap: 2
          }}
        >
          <TextField
            label="Customer Name"
            value={form.customer.name}
            onChange={(e) =>
              updateCustomer('name', e.target.value)
            }
          />

          <TextField
            label="Mobile Number"
            value={form.customer.mobile}
            onChange={(e) =>
              updateCustomer('mobile', e.target.value)
            }
          />

          <TextField
            label="Reference Staff"
            value={form.customer.reference_staff}
            onChange={(e) =>
              updateCustomer('reference_staff', e.target.value)
            }
          />
        </Box>

        {/* RIGHT */}
        <Box
          sx={{
            display: 'grid',
            gap: 2
          }}
        >
          <Select
            value={form.customer.cheque_type}
            onChange={(e) =>
              updateCustomer('cheque_type', e.target.value)
            }
          >
            {chequeTypes.map(c => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>

          <TextField
            label="From Bank"
            value={form.customer.from_bank}
            onChange={(e) =>
              updateCustomer('from_bank', e.target.value)
            }
          />

          <TextField
            label="To Bank / SRO"
            value={form.customer.to_bank_sro}
            onChange={(e) =>
              updateCustomer('to_bank_sro', e.target.value)
            }
          />
        </Box>

      </Box>

      {/* 🔥 ACKNOWLEDGEMENT */}
      <Divider sx={{ mb: 2 }} />

      <Typography
        fontWeight={600}
        mb={2}
      >
        Original Document / Cheque Acknowledgement Status
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 2,
          mb: 3
        }}
      >
        <TextField
          label="Acknowledgement Handovered To"
          value={form.acknowledgement.handover_to}
          onChange={(e) =>
            updateAcknowledgement(
              'handover_to',
              e.target.value
            )
          }
        />

        <TextField
          label="Acknowledgement Handovered By"
          value={form.acknowledgement.handover_by}
          onChange={(e) =>
            updateAcknowledgement(
              'handover_by',
              e.target.value
            )
          }
        />
      </Box>

      {/* 🔥 DOCUMENT DETAILS */}
      <Divider sx={{ mb: 2 }} />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography fontWeight={600}>
          Document Details
        </Typography>

        <Button
          variant="contained"
          onClick={() => {
            setEditingDoc({
              ...emptyDoc,
              id: Date.now()
            });

            setDialogOpen(true);
          }}
        >
          Add Document
        </Button>
      </Box>

      <Box
        sx={{
          height: 350,
          mb: 4
        }}
      >
        <DataGrid
          rows={form.document_details}
          columns={columns}
          hideFooter
          onRowDoubleClick={(params) =>
            handleEditDoc(params.row)
          }
        />
      </Box>

      {/* 🔥 EXPENSES */}
      <Divider sx={{ mb: 2 }} />

      <Typography
        fontWeight={600}
        mb={2}
      >
        Cheque Handover Expenses
      </Typography>

      {form.expenses.map((row, i) => (

        <Box
          key={i}
          sx={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr',
            gap: 2,
            mb: 2
          }}
        >
          <Typography>
            {row.label}
          </Typography>

          <TextField
            type="number"
            value={row.amount}
            onChange={(e) =>
              updateExpense(i, e.target.value)
            }
          />
        </Box>
      ))}

      {/* 🔥 TOTAL */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          px: 2,
          py: 2,
          mt: 2,
          background: '#fef3c7',
          borderRadius: 2
        }}
      >
        <Typography fontWeight={700}>
          Total
        </Typography>

        <Typography fontWeight={700}>
          ₹ {total}
        </Typography>
      </Box>

      {/* 🔥 DOCUMENT DIALOG */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >

        <DialogTitle>
          Document Details
        </DialogTitle>

        <DialogContent>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
              mt: 1
            }}
          >

            <TextField
              label="Doc Date & Number"
              value={editingDoc.doc_date_number}
              onChange={(e) =>
                setEditingDoc({
                  ...editingDoc,
                  doc_date_number: e.target.value
                })
              }
            />

            <TextField
              label="Description"
              value={editingDoc.description}
              onChange={(e) =>
                setEditingDoc({
                  ...editingDoc,
                  description: e.target.value
                })
              }
            />

            <Select
              value={editingDoc.document_type}
              onChange={(e) =>
                setEditingDoc({
                  ...editingDoc,
                  document_type: e.target.value
                })
              }
            >
              {documentTypes.map(d => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </Select>

            <Select
              value={editingDoc.document_status}
              onChange={(e) =>
                setEditingDoc({
                  ...editingDoc,
                  document_status: e.target.value
                })
              }
            >
              {documentStatuses.map(d => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </Select>

            <TextField
              label="Handovered To"
              value={editingDoc.handover_to}
              onChange={(e) =>
                setEditingDoc({
                  ...editingDoc,
                  handover_to: e.target.value
                })
              }
            />

            <TextField
              label="Handovered To Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={editingDoc.handover_to_date}
              onChange={(e) =>
                setEditingDoc({
                  ...editingDoc,
                  handover_to_date: e.target.value
                })
              }
            />

            <TextField
              label="Handovered By"
              value={editingDoc.handover_by}
              onChange={(e) =>
                setEditingDoc({
                  ...editingDoc,
                  handover_by: e.target.value
                })
              }
            />

            <TextField
              label="Handovered By Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={editingDoc.handover_by_date}
              onChange={(e) =>
                setEditingDoc({
                  ...editingDoc,
                  handover_by_date: e.target.value
                })
              }
            />

          </Box>

        </DialogContent>

        <DialogActions>

          <Button
            onClick={() => setDialogOpen(false)}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={saveDocument}
          >
            Save
          </Button>

        </DialogActions>

      </Dialog>

    </Box>
  );
};

export default ChequeHandoverForm;